import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { defaultQueryFields } from '../routes/assets/assetList';

const STORAGE_KEYS = {
    searchParams: 'assetList_searchParams',
    filterParams: 'assetList_filterParams'
};

export const clearAssetListPersistedFilters = () => {
    localStorage.removeItem(STORAGE_KEYS.searchParams);
    localStorage.removeItem(STORAGE_KEYS.filterParams);
};

export const URL_KEYS = {
    searchParams: ['query', 'fields'],
    filterParams: ['manufacturer', 'productCategory', 'productModel', 'product', 'iotConnectionStatus', 'iotOverallStatus', 'marketSegment', 'applicationType', 'waferType', 'processSeverity', 'strategicTool', 'region', 'client'],
};

const hasAnyUrlParam = (urlParams: URLSearchParams, keys: string[]) =>
    keys.some((key) => urlParams.has(key));

export const useSyncedAssetListParams = () => {
    const [urlParams, setUrlParams] = useSearchParams();

    const getInitialSearchParams = () => {
        if (hasAnyUrlParam(urlParams, URL_KEYS.searchParams)) {
            return {
                query: urlParams.get('query') || '',
                fields: urlParams.getAll('fields') || defaultQueryFields,
            };
        }

        const local = localStorage.getItem(STORAGE_KEYS.searchParams);
        return local ? JSON.parse(local) : { query: '', fields: defaultQueryFields };
    };

    const getInitialFilterParams = () => {
        if (hasAnyUrlParam(urlParams, URL_KEYS.filterParams)) {
            return {
                manufacturer: urlParams.get('manufacturer'),
                productCategory: urlParams.get('productCategory'),
                productModel: urlParams.get('productModel'),
                product: urlParams.get('product'),
                iotConnectionStatus: urlParams.get('iotConnectionStatus'),
                iotOverallStatus: urlParams.get('iotOverallStatus'),
                marketSegment: urlParams.get('marketSegment'),
                applicationType: urlParams.get('applicationType'),
                waferType: urlParams.get('waferType'),
                processSeverity: urlParams.get('processSeverity'),
                strategicTool: urlParams.get('strategicTool'),
                region: urlParams.get('region'),
                client: urlParams.get('client'),
            };
        }

        const local = localStorage.getItem(STORAGE_KEYS.filterParams);
        return local
            ? JSON.parse(local)
            : {
                manufacturer: null,
                productCategory: null,
                productModel: null,
                product: null,
                iotConnectionStatus: null,
                iotOverallStatus: null,
                marketSegment: null,
                applicationType: null,
                waferType: null,
                processSeverity: null,
                strategicTool: null,
                region: null,
                client: null,
            };
    };

    const [searchParams, setSearchParams] = useState(getInitialSearchParams);
    const [filterParams, setFilterParams] = useState(getInitialFilterParams);

    // Sync to localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.searchParams, JSON.stringify(searchParams));
    }, [searchParams]);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.filterParams, JSON.stringify(filterParams));
    }, [filterParams]);

    // On first load: if no URL params, apply localStorage values into URL
    useEffect(() => {
        if (
            !hasAnyUrlParam(urlParams, [
                ...URL_KEYS.searchParams,
                ...URL_KEYS.filterParams
            ])
        ) {
            const combinedParams = {
                ...getInitialSearchParams(),
                ...getInitialFilterParams(),
            };

            for (const key in combinedParams) {
                const value = combinedParams[key];
                if (value !== null && value !== undefined) {
                    if (Array.isArray(value)) {
                        urlParams.delete(key);
                        value.forEach((v) => urlParams.append(key, v));
                    } else {
                        urlParams.set(key, value);
                    }
                }
            }

            setUrlParams(urlParams, { replace: true });
        }
    }, []);

    const resetFilterParams = () => {
        // 1. Alles aus URL löschen
        [...URL_KEYS.searchParams, ...URL_KEYS.filterParams].forEach((key) => {
            urlParams.delete(key);
        });
        setUrlParams(urlParams, { replace: true });

        // 2. LocalStorage leeren
        localStorage.removeItem(STORAGE_KEYS.searchParams);
        localStorage.removeItem(STORAGE_KEYS.filterParams);

        // 3. State zurücksetzen
        setSearchParams({
            query: '',
            fields: defaultQueryFields
        });
        setFilterParams({
            manufacturer: null,
            productCategory: null,
            productModel: null,
            product: null,
            iotConnectionStatus: null,
            iotOverallStatus: null,
            marketSegment: null,
            applicationType: null,
            waferType: null,
            processSeverity: null,
            strategicTool: null,
            client: null
        });
    };

    return {
        urlParams,
        setUrlParams,
        searchParams,
        setSearchParams,
        filterParams,
        setFilterParams,
        resetFilterParams
    };
};
